import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/use-user-role";
import { getAuthenticatedLocalClient } from "@/lib/supabase-clients";

function getLocalUserIdFromToken(localToken: string): string | null {
  try {
    const payload = JSON.parse(atob(localToken.split(".")[1]));
    return payload.sub || null;
  } catch {
    return null;
  }
}

/**
 * Resolves the access "source" for the current user:
 *  - client / client_test → user_owner_access.source
 *  - owner  / owner_test  → user_listing_access.source
 *  - other roles → null (no restriction applies)
 *
 * Per product rule, a single restricted user only ever has one source value,
 * so we read the first non-null row.
 */
export function useAccessSource() {
  const { localToken } = useAuth();
  const { primaryRole, isLoading: roleLoading } = useUserRole();
  const localUserId = localToken ? getLocalUserIdFromToken(localToken) : null;

  const restrictedRole =
    primaryRole === "client" ||
    primaryRole === "client_test" ||
    primaryRole === "owner" ||
    primaryRole === "owner_test";

  const table =
    primaryRole === "client" || primaryRole === "client_test"
      ? "user_owner_access"
      : primaryRole === "owner" || primaryRole === "owner_test"
      ? "user_listing_access"
      : null;

  const { data: source = null, isLoading: queryLoading } = useQuery({
    queryKey: ["user_access_source", localUserId, table],
    queryFn: async (): Promise<string | null> => {
      const client = getAuthenticatedLocalClient();
      if (!client || !localUserId || !table) return null;
      const { data, error } = await client
        .from(table)
        .select("source")
        .eq("user_id", localUserId)
        .not("source", "is", null)
        .limit(1);
      if (error) {
        console.error("[useAccessSource] Query error:", error);
        throw error;
      }
      return (data?.[0]?.source as string | null) ?? null;
    },
    enabled: !!localToken && !!localUserId && !!table,
    staleTime: 5 * 60 * 1000,
  });

  const isPricelabs = restrictedRole && source === "pricelabs";

  return {
    source,
    isPricelabs,
    isSourceRestricted: restrictedRole,
    isLoading: roleLoading || (restrictedRole && queryLoading),
  };
}
